/**
 * 2-of-2 XOR one-time split of the session root.
 *
 * `share_A` is fresh CSPRNG bytes; `share_B = R XOR share_A`. Because `share_A`
 * is uniform and independent of `R`, each share on its own is a one-time pad of
 * the other and reveals *nothing* about `R` (information-theoretic secrecy):
 * for any candidate root R', there is exactly one `share_B` consistent with a
 * given `share_A`, so a single share is equally consistent with every possible
 * root. Only the XOR of both shares recovers `R`.
 *
 * XOR alone provides no integrity, so both shares additionally carry a shared
 * SHA-256 commitment that binds `R`, `share_A`, and `share_B` together:
 *
 *   commitment = SHA-256(
 *     UTF8("fume.share.commit.v1")
 *     || uint64be(R.length)       || R
 *     || uint64be(share_A.length) || share_A
 *     || uint64be(share_B.length) || share_B
 *   )
 *
 * `reconstructRoot` recomputes and checks this commitment, so any tampered,
 * swapped, or mismatched share is rejected instead of yielding a corrupt root.
 */
import { sodium } from './backend.js';
import { sha256 } from './hash.js';
import { FumeCryptoError } from './errors.js';
import { SESSION_ROOT_SIZE, SHARE_COMMITMENT_SIZE, SHARE_COMMIT_DOMAIN, SHARE_INDEX_A, SHARE_INDEX_B, SHARE_SIZE, } from './constants.js';
import { bytesEqual, concatBytes, requireLength, uint64be } from './internal.js';
const utf8 = new TextEncoder();
/** XOR two equal-length byte arrays into a fresh `Uint8Array`. */
function xorBytes(a, b) {
    const out = new Uint8Array(a.length);
    for (let i = 0; i < a.length; i++)
        out[i] = a[i] ^ b[i];
    return out;
}
/**
 * Cross-share commitment binding `R`, `share_A`, and `share_B` per §2.1.
 * Exported for callers (and tests) that need to build a share by hand.
 */
export function shareCommitment(R, shareA, shareB) {
    const preimage = concatBytes(utf8.encode(SHARE_COMMIT_DOMAIN), uint64be(R.length), R, uint64be(shareA.length), shareA, uint64be(shareB.length), shareB);
    return sha256(preimage);
}
/**
 * Split `R` into a 2-of-2 XOR pair. `share_A` is fresh CSPRNG bytes and
 * `share_B = R XOR share_A`; both shares carry the identical cross-share
 * commitment.
 *
 * @throws {FumeCryptoError} if `R` is not exactly {@link SESSION_ROOT_SIZE} bytes.
 */
export function splitRoot(R) {
    requireLength(R, SESSION_ROOT_SIZE, 'R');
    const shareA = sodium.randombytes_buf(SHARE_SIZE);
    const shareB = xorBytes(R, shareA);
    const commitment = shareCommitment(R, shareA, shareB);
    return [
        { index: SHARE_INDEX_A, share: shareA, commitment },
        { index: SHARE_INDEX_B, share: shareB, commitment: Uint8Array.from(commitment) },
    ];
}
function validateShare(raw, name) {
    if (raw == null || typeof raw !== 'object') {
        throw new FumeCryptoError(`${name} must be a RawShare object`);
    }
    if (raw.index !== SHARE_INDEX_A && raw.index !== SHARE_INDEX_B) {
        throw new FumeCryptoError(`${name}.index must be ${SHARE_INDEX_A} or ${SHARE_INDEX_B}`);
    }
    requireLength(raw.share, SHARE_SIZE, `${name}.share`);
    requireLength(raw.commitment, SHARE_COMMITMENT_SIZE, `${name}.commitment`);
}
/**
 * Reconstruct `R` from a 2-of-2 share pair, verifying the cross-share
 * commitment. Rejects missing, malformed, tampered, swapped, or mismatched
 * shares.
 *
 * @throws {FumeCryptoError} on any of the failure conditions in §2.1.
 */
export function reconstructRoot(shareA, shareB) {
    // 1. Both shares must be present, well-formed, and correctly indexed.
    validateShare(shareA, 'shareA');
    validateShare(shareB, 'shareB');
    if (shareA.index !== SHARE_INDEX_A || shareB.index !== SHARE_INDEX_B) {
        throw new FumeCryptoError('shares must have index 1 (A) and 2 (B) respectively');
    }
    // 2. Both shares must agree on the commitment.
    if (!bytesEqual(shareA.commitment, shareB.commitment)) {
        throw new FumeCryptoError('share commitments do not match');
    }
    // 3. XOR the shares back together.
    const R = xorBytes(shareA.share, shareB.share);
    // 4-5. Recompute the commitment from R' and both shares; reject on mismatch.
    const recomputed = shareCommitment(R, shareA.share, shareB.share);
    if (!bytesEqual(recomputed, shareA.commitment)) {
        throw new FumeCryptoError('share commitment verification failed (tampered or mismatched shares)');
    }
    // 6. Success.
    return R;
}
//# sourceMappingURL=split.js.map